import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api, { getCourseById, loginUser } from '../api';
import './CourseDetail.css';
import ChatWidget from '../components/ChatWidget';

const CourseDetail = () => {
  const { id } = useParams();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]); 
  const [showWelcome, setShowWelcome] = useState(false);
  
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState(null);

  // ESTADO PARA LOS MÓDULOS DESPLEGABLES (Acordeón)
  const [expandedModules, setExpandedModules] = useState({});

  const PAYPAL_LINK = "https://paypal.me/iamjhonatans"; 
  const WHATSAPP_SOPORTE = "https://wa.me/51963462787";

  useEffect(() => {
    const fetchCourseAndValidate = async () => {
      try {
        const response = await getCourseById(id);
        const courseData = response.data;
        setCourse(courseData);
        
        if (courseData.lessons && courseData.lessons.length > 0) {
          setActiveLesson(courseData.lessons[0]);
          
          // Abrir el primer módulo por defecto
          const firstModule = courseData.lessons[0].module_title || "Módulo 1. Introducción";
          setExpandedModules({ [firstModule]: true });
        }

        const token = localStorage.getItem('token');
        const storedUserId = localStorage.getItem('userId');
        const storedCourses = localStorage.getItem('allowedCourses');
        const storedUser = localStorage.getItem('userName');
        
        if (token && storedCourses && storedUserId && storedUserId !== "null") {
          const allowedIDs = JSON.parse(storedCourses);
          const currentID = parseInt(id);

          if (allowedIDs.includes(currentID)) {
            setIsLoggedIn(true);
            setUserName(storedUser || "Alumno");
            setUserId(storedUserId);

            const progressRes = await api.get(`/courses/user/${storedUserId}/progress/${currentID}`);
            setCompletedLessons(progressRes.data);
          }
        }
      } catch (err) {
        if (err.response?.status !== 422) {
            setError("Error al conectar con el aula virtual. Intenta de nuevo.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCourseAndValidate();
  }, [id]);

  // --- NUEVO FIX: AUTO-SCROLL SUAVE ---
  // Este código "escucha" cada vez que activeLesson cambia.
  // Cuando el alumno hace clic en una lección, React espera a dibujarla y luego sube la pantalla.
  useEffect(() => {
    if (activeLesson) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeLesson]);
  // ------------------------------------
  
  const toggleLessonCompleted = async (lessonId) => {
    if (!userId) return;
    try {
      const res = await api.post(`/courses/lessons/${lessonId}/toggle-complete?user_id=${userId}`);
      if (res.data.status === "marked") {
        setCompletedLessons([...completedLessons, lessonId]);
      } else {
        setCompletedLessons(completedLessons.filter(lid => lid !== lessonId));
      }
    } catch (err) {
      console.error("Error al guardar progreso en DB:", err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    try {
      const response = await loginUser(loginData);
      const { access_token, user_name, user_id, enrolled_courses } = response.data;
      const currentCourseId = parseInt(id);

      if (enrolled_courses && enrolled_courses.includes(currentCourseId)) {
        localStorage.setItem('token', access_token);
        localStorage.setItem('userName', user_name);
        localStorage.setItem('userId', user_id);
        localStorage.setItem('allowedCourses', JSON.stringify(enrolled_courses));
        
        setUserId(user_id);
        setUserName(user_name);
        setIsLoggedIn(true);
        setShowWelcome(true);
        
        const progressRes = await api.get(`/courses/user/${user_id}/progress/${currentCourseId}`);
        setCompletedLessons(progressRes.data);
        setTimeout(() => setShowWelcome(false), 4000);
      } else {
        setLoginError("Tu cuenta es válida, pero este curso no está en tu lista de compras.");
      }
    } catch (err) {
      setLoginError("Credenciales incorrectas o problema de servidor.");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setUserId(null);
    setUserName("");
    setCompletedLessons([]);
    window.location.reload(); 
  };

  const handleInputChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  const getDiscountPrice = (price) => (price * 0.8).toFixed(2); 
  const getWhatsAppProofLink = () => {
    if (!course) return "#";
    const message = `Hola Bull Miner, adjunto mi comprobante para el curso "${course.title}" (Precio Promo: $${getDiscountPrice(course.price)}). Mi correo es: ...`;
    return `${WHATSAPP_SOPORTE}?text=${encodeURIComponent(message)}`;
  };
  const getSupportLink = () => `${WHATSAPP_SOPORTE}?text=Hola%20Soporte%2C%20tengo%20una%20consulta%20sobre%20el%20aula%20virtual.`;
  const getStudentGroupLink = () => "https://chat.whatsapp.com/FiIsFNaZeJQLFPGX3LI3Ls"; 

  if (loading) return <div className="loading-screen">Cargando aula virtual...</div>;
  if (error) return <div className="error-screen">{error}</div>;
  if (!course) return <div className="error-screen">Curso no encontrado.</div>;

  const isMineria = course.category === 'mineria';
  const accentColor = isMineria ? '#FFD700' : '#2EA043'; 
  const totalLessons = course?.lessons?.length || 0;
  const completedCount = completedLessons.length;
  const progressPercentage = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  // AGRUPAR LECCIONES POR MÓDULO
  const groupedLessons = course.lessons?.reduce((acc, lesson) => {
    const modName = lesson.module_title || "Módulo 1. Introducción";
    if (!acc[modName]) acc[modName] = [];
    acc[modName].push(lesson);
    return acc;
  }, {});

  // --- LÓGICA PARA EXTRAER LOS LINKS OCULTOS ---
  let displayDescription = course?.description || "";
  let courseResources = [];
  if (displayDescription.includes('|||RECURSOS|||')) {
      const parts = displayDescription.split('|||RECURSOS|||');
      displayDescription = parts[0];
      try {
          courseResources = JSON.parse(parts[1]);
      } catch(e) {
          console.error("Error leyendo recursos", e);
      }
  }
  // ---------------------------------------------

  return (
    <div className="course-detail-container">
      {showWelcome && (
        <div className="welcome-popup-overlay">
          <div className="welcome-popup">
            <div className="check-icon">🎉</div>
            <h2>¡Bienvenido, {userName}!</h2>
            <p>Tu progreso se guardará automáticamente en cada sesión.</p>
          </div>
        </div>
      )}

      <div className="course-hero-split">
        <div className="course-hero-info">
          <div className="breadcrumb">
            <span>Cursos</span> / <span style={{color: accentColor, fontWeight:'bold', textTransform:'capitalize'}}>{course.category}</span>
          </div>
    
          <h1 className="course-title" style={{ color: course.category === 'geologia' ? '#2EA043' : '#FFFFFF' }}>
            {course.title}
          </h1>
          
          {isLoggedIn && activeLesson ? (
            <div className="lesson-player-container">
              
              {/* FIX: SOLO MUESTRA EL REPRODUCTOR SI HAY UN LINK DE VIDEO */}
              {activeLesson.video_url && (
                <div className="video-wrapper">
                  <iframe 
                    src={activeLesson.video_url} 
                    title={activeLesson.title}
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              
              <div className="lesson-meta-box">
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                  <h3 style={{margin:0, color:'#FFD700'}}>{activeLesson.title}</h3>
                  <button 
                    onClick={() => toggleLessonCompleted(activeLesson.id)}
                    style={{
                      backgroundColor: completedLessons.includes(activeLesson.id) ? '#238636' : 'transparent',
                      border: '1px solid #238636',
                      color: completedLessons.includes(activeLesson.id) ? 'white' : '#238636',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    {completedLessons.includes(activeLesson.id) ? (
                      <><i className="fas fa-check"></i> Completada</>
                    ) : (
                      <><i className="far fa-circle"></i> Marcar como vista</>
                    )}
                  </button>
                </div>

                <p style={{color:'#8B949E', fontStyle:'italic'}}>{activeLesson.subtitle}</p>
                <p style={{marginTop:'10px'}}>{activeLesson.content}</p>
                
                {activeLesson.resources && activeLesson.resources.length > 0 && (
                  <div className="resources-section">
                    <h4><i className="fas fa-folder-open"></i> Recursos de la clase</h4>
                    <div className="resources-list">
                      {activeLesson.resources.map(res => (
                        <a href={res.file_url} target="_blank" rel="noopener noreferrer" className="resource-item" key={res.id}>
                          <div className={`resource-icon type-${res.resource_type}`}>
                            <i className="fas fa-file"></i>
                          </div>
                          <div className="resource-info">
                            <span className="resource-name">{res.title}</span>
                            <span className="resource-action">Descargar</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="student-support-area">
                  <span>¿Dudas sobre esta sesión?</span>
                  <a href={getStudentGroupLink()} target="_blank" rel="noopener noreferrer" className="btn-student-group">
                    <i className="fab fa-whatsapp"></i> Preguntar en la Comunidad
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="course-intro-text">
              <p className="course-description-main">{displayDescription}</p>
              <div className="course-meta-icons">
                <span><i className="fas fa-signal"></i> Nivel: {course.level || "Intermedio"}</span>
                <span><i className="far fa-clock"></i> {course.duration_hours || 10} horas</span>
                <span><i className="fas fa-certificate"></i> Certificado</span>
              </div>
            </div>
          )}

          {/* SYLLABUS VISUAL - ACORDEÓN POR MÓDULOS */}
          <section className="syllabus-section">
            <h2 style={{marginBottom: '30px'}}>CONTENIDO DEL CURSO</h2>
            
            <div className="modules-accordion-container">
              {groupedLessons && Object.entries(groupedLessons).map(([moduleName, lessonsInModule], modIndex) => {
                const isExpanded = expandedModules[moduleName];
                
                return (
                  <div key={moduleName} className="module-wrapper">
                    
                    {/* CABECERA DEL MÓDULO */}
                    <div className="module-header" onClick={() => toggleModule(moduleName)}>
                      <div className="module-header-info">
                        <span className="module-subtitle">
                          Módulo {modIndex + 1} &nbsp;▶&nbsp; {lessonsInModule.length} Sesiones &nbsp;
                          <span style={{color: '#2EA043'}}>● Disponible: Ahora</span>
                        </span>
                        <h3 className="module-title-text">{moduleName}</h3>
                      </div>
                      <div className={`module-chevron ${isExpanded ? 'expanded' : ''}`}>
                        <i className="fas fa-chevron-left"></i>
                      </div>
                    </div>
                    
                    {/* LISTA DE LECCIONES (Se muestra si está expandido) */}
                    {isExpanded && (
                      <div className="module-content">
                        {lessonsInModule.map((lesson, index) => {
                          const isFinished = completedLessons.includes(lesson.id);
                          const isCurrent = activeLesson?.id === lesson.id;
                          
                          return (
                            <div 
                              key={lesson.id} 
                              className={`lesson-row-accordion ${isLoggedIn ? 'unlocked' : 'locked'} ${isCurrent ? 'active' : ''}`}
                              onClick={() => isLoggedIn && setActiveLesson(lesson)}
                            >
                              <div className="lesson-play-icon">
                                {isFinished ? (
                                  <i className="fas fa-check-circle" style={{color:'#2EA043'}}></i>
                                ) : (
                                  isLoggedIn ? <i className="fas fa-play"></i> : <i className="fas fa-lock"></i>
                                )}
                              </div>
                              <div className="lesson-title-accordion">
                                {index + 1}. {lesson.title}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {course.instructor && (
            <section className="instructor-detail-section">
              <h2>Conoce a tu Instructor</h2>
              <div className="instructor-detail-card">
                <img src={course.instructor.photo_url} alt={course.instructor.name} />
                <div className="instructor-bio-content">
                  <h3>{course.instructor.name}</h3>
                  <p>{course.instructor.bio}</p>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="course-sidebar-form">
          {isLoggedIn ? (
            <div className="form-card instructor-mini-card">
              <h3>Tu Avance General</h3>
              <div className="progress-bar-container" style={{backgroundColor:'#0D1117', padding:'15px', borderRadius:'8px', marginBottom:'20px'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px', fontSize:'0.9rem'}}>
                  <span>Progreso</span>
                  <span style={{color: '#2EA043', fontWeight:'bold'}}>{progressPercentage}%</span>
                </div>
                <div className="progress-bar" style={{height:'8px', backgroundColor:'#30363D', borderRadius:'4px', overflow:'hidden'}}>
                  <div className="progress-fill" style={{ width: `${progressPercentage}%`, backgroundColor: '#2EA043', height: '100%', transition: 'width 0.5s ease' }}></div>
                </div>
                <p style={{fontSize:'0.8rem', color:'#8B949E', marginTop:'10px', textAlign:'center'}}>
                  {completedCount} de {totalLessons} lecciones vistas
                </p>
              </div>
              <a href={getSupportLink()} target="_blank" rel="noopener noreferrer" className="btn-support-sidebar">
                <i className="fas fa-headset"></i> Soporte Técnico
              </a>
              <button onClick={handleLogout} className="btn-logout-sidebar">
                <i className="fas fa-sign-out-alt"></i> Cerrar Sesión
              </button>
            </div>
          ) : (
            <div className="form-card">
              <div className="payment-option-box">
                <div className="payment-header">
                  <span className="payment-title">Pago PayPal</span>
                  <span className="payment-price">${course.price} USD</span>
                </div>
                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" className="btn-pay-online">Pagar con PayPal</a>
              </div>

              <div className="payment-option-box discount-box">
                <div className="discount-badge">PROMO BCP</div>
                <div className="payment-header">
                  <span className="payment-title">Transferencia</span>
                  <span className="payment-price discount-price">${getDiscountPrice(course.price)} USD</span>
                </div>
                {!showBankInfo ? (
                  <button className="btn-show-bank" onClick={() => setShowBankInfo(true)}>Ver Datos BCP</button>
                ) : (
                  <div className="bank-info-container">
                    <p style={{fontSize:'0.85rem'}}>BCP: <strong>Maycol Jhonatan Benavides S.</strong></p>
                    <p style={{fontSize:'0.85rem', color:'#FFD700'}}>Soles: 570-11743399-0-72</p>
                    <p style={{fontSize:'0.85rem', color:'#FFD700'}}>Dólares: 570-11745078-1-68</p>
                    <a href={getWhatsAppProofLink()} target="_blank" rel="noopener noreferrer" className="btn-pay-direct">Enviar Comprobante</a>
                  </div>
                )}
              </div>
              
              <div className="divider-or"><span>Acceso Alumnos</span></div>

              <form onSubmit={handleLogin} className="login-form-sidebar">
                {loginError && <div className="login-error-msg"><i className="fas fa-exclamation-circle"></i> {loginError}</div>}
                <div className="form-group">
                  <input type="email" name="email" value={loginData.email} onChange={handleInputChange} placeholder="Correo electrónico" required />
                </div>
                <div className="form-group">
                  <input type="password" name="password" value={loginData.password} onChange={handleInputChange} placeholder="Contraseña" required />
                </div>
                <button type="submit" className="btn-login-sidebar">INGRESAR AL AULA</button>
              </form>
            </div>
          )}

          {/* TARJETA INDEPENDIENTE DEL MATERIAL DEL CURSO */}
          {courseResources.length > 0 && (
            <div className="form-card course-material-card">
              <h3 className="material-card-title">
                <i className="fas fa-file-pdf"></i> Material del Curso
              </h3>
              <div className="material-links-container">
                {courseResources.map((res, idx) => (
                  <a key={idx} href={res.link} target="_blank" rel="noopener noreferrer" className="material-link-item">
                    <div className="material-icon-badge">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <span className="material-link-title">{res.title || 'Brochure / Documento'}</span>
                    <i className="fas fa-external-link-alt material-external-icon"></i>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoggedIn && activeLesson && (
        <ChatWidget lessonId={activeLesson.id} lessonTitle={activeLesson.title} />
      )}
    </div>
  );
};

export default CourseDetail;
